import { useEffect, useState } from "react";
import { allianceCan } from "@zend/content";
import { Button } from "../ui/Button.js";
import { Panel } from "../ui/Panel.js";
import { Icon } from "../ui/Icon.js";
import { formatDuration, useServerTick } from "../ui/Timer.js";
import { getLocale, useT } from "../i18n/index.js";
import { relativeTime } from "../social/mailModel.js";
import { allianceApi } from "./api.js";
import { useAlliance } from "./state.js";
import type { AllianceListEntry, AllianceView, ProposalView, RelationView } from "./types.js";

/** Diplomacy tab: relations with timers and actions by role, proposals in and out, history, and a search to start a relation (82-alliance.md R79, D73). */
export function Diplomacy({ alliance }: { alliance: AllianceView }) {
  const t = useT();
  const d = useAlliance((s) => s.diplomacy);
  const refresh = useAlliance((s) => s.refreshDiplomacy);
  const now = useServerTick(1);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  if (!d) return <p className="hint" style={{ padding: 12 }}>{t("common.loading")}</p>;
  const canAct = alliance.myRole !== null && allianceCan(alliance.myRole, "diplomacy") && d.canAct;
  return (
    <div className="alliance-diplomacy" data-testid="alliance-diplomacy">
      <Panel title={t("alliance.diplomacy.title")}>
        <p className="hint">{t("alliance.diplomacy.hint")}</p>
        {!canAct ? <p className="hint" data-testid="diplomacy-officers-only">{t("alliance.diplomacy.officersOnly")}</p> : null}
        {canAct && d.embassy < 1 ? <p className="hint" data-testid="diplomacy-embassy-hint">{t("alliance.diplomacy.embassyHint")}</p> : null}
        {d.relations.length === 0 ? <p className="hint" data-testid="diplomacy-none">{t("alliance.diplomacy.none")}</p> : null}
        {d.relations.map((r) => <RelationRow key={r.allianceId} r={r} now={now} canAct={canAct} embassy={d.embassy} />)}
      </Panel>
      {canAct ? <SearchAlliances own={alliance.id} known={new Set(d.relations.map((r) => r.allianceId))} embassy={d.embassy} /> : null}
      <Panel title={t("alliance.diplomacy.proposals")}>
        {d.proposals.incoming.length === 0 && d.proposals.outgoing.length === 0 ? <p className="hint" data-testid="diplomacy-no-proposals">{t("alliance.diplomacy.noProposals")}</p> : null}
        {d.proposals.incoming.map((p) => <ProposalRow key={p.id} p={p} incoming canAct={canAct} now={now} />)}
        {d.proposals.outgoing.map((p) => <ProposalRow key={p.id} p={p} incoming={false} canAct={canAct} now={now} />)}
      </Panel>
      <Panel title={t("alliance.diplomacy.history")}>
        {d.history.length === 0 ? <p className="hint">{t("alliance.diplomacy.noHistory")}</p> : null}
        <div className="diplomacy-history" data-testid="diplomacy-history">
          {d.history.map((l) => {
            const p = l.payload as { event?: string; name?: string; tag?: string; kind?: string; winner?: string | null };
            return (
              <div key={l.id} className="row" data-event={p.event}>
                <span className="grow">{t(`alliance.diplomacy.event.${p.event ?? "neutral"}`, { name: p.name ?? "?", tag: p.tag ?? "?", kind: p.kind ?? "", winner: p.winner ?? "—" })}{l.actorName ? ` · ${l.actorName}` : ""}</span>
                <span className="hint mono">{relativeTime(l.createdAt, now, getLocale())}</span>
              </div>
            );
          })}
        </div>
      </Panel>
    </div>
  );
}

function RelationRow({ r, now, canAct, embassy }: { r: RelationView; now: number; canAct: boolean; embassy: number }) {
  const t = useT();
  const act = useAlliance((s) => s.diplomacyAct);
  const left = (at: number | null) => formatDuration(Math.max(0, (at ?? now) - now));
  const countdown = r.state === "hostile" && r.pending === "war";
  const breaking = r.state === "allied" && r.pending === "neutral";
  let timer: string | null = null;
  if (breaking) timer = t("alliance.diplomacy.breaking", { t: left(r.until) });
  else if (countdown) timer = t("alliance.diplomacy.countdown", { t: left(r.until) });
  else if (r.state === "war") timer = t("alliance.diplomacy.warUntil", { t: left(r.until) });
  const gateOpen = r.warGate?.ok ?? false;
  const neutralReady = r.neutralAt !== null && now >= r.neutralAt;
  return (
    <div className={`diplomacy-row banner-${r.banner}`} data-testid={`relation-${r.allianceId}`} data-state={r.state} data-pending={r.pending ?? ""}>
      <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
        <span className="alliance-tag small">{r.tag}</span>
        <strong className="grow ellipsis">{r.name}</strong>
        <span className={`relation-pill relation-${r.state}`} data-testid={`relation-state-${r.allianceId}`}>{t(`alliance.relation.${r.state}`)}</span>
      </div>
      {timer ? <div className="hint mono" data-testid={`relation-timer-${r.allianceId}`}>{timer}</div> : null}
      {r.state === "war" || countdown ? <div className="hint" data-testid={`relation-score-${r.allianceId}`}>{t("alliance.diplomacy.score", { mine: r.warScore.mine, theirs: r.warScore.theirs })}</div> : null}
      {r.state === "hostile" && !countdown ? (
        <div className="hint">
          {neutralReady ? null : <span>{t("alliance.diplomacy.neutralAt", { t: left(r.neutralAt) })} · </span>}
          {gateOpen ? <span>{t("alliance.diplomacy.gateOpen")}</span> : <span>{t("alliance.diplomacy.gateHostile", { t: left(r.warGate?.at ?? null) })} · {t("alliance.diplomacy.gateRaids", { n: r.warGate?.raids ?? 0, max: 3 })}</span>}
        </div>
      ) : null}
      {canAct ? (
        <div className="actions">
          {r.state === "allied" && !breaking ? <Button style={{ minHeight: 34 }} onClick={() => void act.neutral(r.allianceId)} data-testid={`relation-neutral-${r.allianceId}`}>{t("alliance.diplomacy.dissolve")}</Button> : null}
          {r.state === "hostile" && !countdown ? (
            <>
              <Button style={{ minHeight: 34 }} disabled={!neutralReady} onClick={() => void act.neutral(r.allianceId)} data-testid={`relation-neutral-${r.allianceId}`}>{t("alliance.diplomacy.neutral")}</Button>
              <Button variant="danger" style={{ minHeight: 34 }} disabled={!gateOpen} onClick={() => void act.war(r.allianceId)} data-testid={`relation-war-${r.allianceId}`}><Icon name="attack" /> {t("alliance.diplomacy.war")}</Button>
            </>
          ) : null}
          {r.state === "war" || countdown ? <Button style={{ minHeight: 34 }} disabled={embassy < 1} onClick={() => void act.propose(r.allianceId, "peace")} data-testid={`relation-peace-${r.allianceId}`}>{t("alliance.diplomacy.peace")}</Button> : null}
          {r.state === "war" ? <Button variant="danger" style={{ minHeight: 34 }} onClick={() => void act.surrender(r.allianceId)} data-testid={`relation-surrender-${r.allianceId}`}>{t("alliance.diplomacy.surrender")}</Button> : null}
        </div>
      ) : null}
    </div>
  );
}

function ProposalRow({ p, incoming, canAct, now }: { p: ProposalView; incoming: boolean; canAct: boolean; now: number }) {
  const t = useT();
  const act = useAlliance((s) => s.diplomacyAct);
  const other = incoming ? p.from : p.to;
  return (
    <div className="diplomacy-row" data-testid={`proposal-${p.id}`} data-kind={p.kind} data-incoming={incoming}>
      <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
        <span className="badge">{incoming ? t("alliance.diplomacy.incoming") : t("alliance.diplomacy.outgoing")}</span>
        <strong className="grow">{t(p.kind === "peace" ? "alliance.diplomacy.proposalPeace" : "alliance.diplomacy.proposalAlliance")}</strong>
        <span className="hint mono">{t("alliance.diplomacy.expires", { t: formatDuration(Math.max(0, p.expiresAt - now)) })}</span>
      </div>
      <div className="hint">{incoming ? t("alliance.diplomacy.from", { name: other.name, tag: other.tag, by: p.createdByName }) : t("alliance.diplomacy.to", { name: other.name, tag: other.tag })}</div>
      {incoming && canAct ? (
        <div className="actions">
          <Button variant="primary" style={{ minHeight: 34 }} onClick={() => void act.respond(p.id, true)} data-testid={`proposal-accept-${p.id}`}>{t("alliance.accept")}</Button>
          <Button style={{ minHeight: 34 }} onClick={() => void act.respond(p.id, false)} data-testid={`proposal-decline-${p.id}`}>{t("alliance.decline")}</Button>
        </div>
      ) : null}
    </div>
  );
}

/** Find an alliance by name or tag and open a relation with it: propose (Embassy 1) or declare hostility. */
function SearchAlliances({ own, known, embassy }: { own: string; known: Set<string>; embassy: number }) {
  const t = useT();
  const act = useAlliance((s) => s.diplomacyAct);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<AllianceListEntry[] | null>(null);
  const search = async () => {
    const list = await allianceApi.list(query.trim()).catch(() => []);
    setResults(list.filter((a) => a.id !== own && !known.has(a.id)));
  };
  return (
    <Panel title={t("alliance.diplomacy.search")}>
      <div className="row" style={{ gap: 8 }}>
        <input className="field grow" value={query} placeholder={t("alliance.find.placeholder")} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") void search(); }} data-testid="diplomacy-search" />
        <Button onClick={() => void search()} data-testid="diplomacy-search-go"><Icon name="search" /></Button>
      </div>
      {results?.length === 0 ? <p className="hint" data-testid="diplomacy-search-none">{t("alliance.find.none")}</p> : null}
      {results?.map((a) => (
        <div key={a.id} className="diplomacy-row" data-testid={`diplomacy-result-${a.id}`}>
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            <span className="alliance-tag small">{a.tag}</span>
            <strong className="grow ellipsis">{a.name}</strong>
            <span className="hint">{t("alliance.level", { n: a.level })} · {a.memberCount}/{a.cap}</span>
          </div>
          <div className="actions">
            <Button variant="primary" style={{ minHeight: 34 }} disabled={embassy < 1} title={embassy < 1 ? t("alliance.diplomacy.embassyHint") : undefined} onClick={() => void act.propose(a.id, "alliance")} data-testid={`diplomacy-propose-${a.id}`}>{t("alliance.diplomacy.propose")}</Button>
            <Button variant="danger" style={{ minHeight: 34 }} onClick={() => void act.hostile(a.id)} data-testid={`diplomacy-hostile-${a.id}`}>{t("alliance.diplomacy.hostile")}</Button>
          </div>
        </div>
      ))}
    </Panel>
  );
}
